import { useState, ChangeEvent, Dispatch, SetStateAction, useCallback } from 'react';
import { FormRenderProps, Form } from 'react-final-form';
import { useDispatch } from 'react-redux';

import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { makeEmailValidation } from 'CorePackage/MainModule/validators/isValidEmail';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { Button } from 'UIPackage/MainModule/components/Button';
import { UploadInput } from 'UIPackage/MainModule/components/UploadInput';
import { UploadStatus } from 'UIPackage/MainModule/components/UploadStatus';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { FeedbackAgreementModal } from 'FeedbackPackage/MainModule/components/FeedbackAgreementModal';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import { makePhoneValidation } from 'CorePackage/MainModule/validators/isValidPhone';
import { IUploadFile } from 'CorePackage/MainModule/interfaces/IUploadFile';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';

import styles from './FeedbackFormBase.module.scss';
import generalStyles from './Feedback.module.scss';
import { FeedbackFormType } from '../types/FeedbackFormType';
import { declarantTypes } from '../constants/declarantTypes';
import { feedbackSetForm } from '../actions/feedbackActions';
import { TextInput } from '../../../UIPackage/MainModule/components/TextInput';
import { IKnmPlan } from '../../../PlanListPackage/MainModule/interfaces/IKnmPlan';
import { approveRequiredSelectOptions } from '../../../KnmErknmPackage/MainModule/constants/approveRequiredSelectOptions';
import { IFeedbackKnmData } from '../interfaces/IFeedbackKnmData';
import { formatDate } from '../../../CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_MINUTE_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { initialFeedbackFormValues } from '../constants/initialFeedbackFormValues';

interface IFeedbackFormBaseProps {
  onFormSubmit: (values: FeedbackFormType) => void;
  handleUploadInputChange: (e: ChangeEvent<HTMLInputElement>, fileName?: string) => void;
  files: IUploadFile[];
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  removeFile: (fileName: string) => void;
  knmData?: IFeedbackKnmData | undefined;
  planData?: IKnmPlan | undefined;
  userInfo: () => string;
  getLink: () => string;
}

export const FeedbackFormBase = ({
  onFormSubmit,
  handleUploadInputChange,
  files,
  error,
  setError,
  removeFile,
  knmData,
  planData,
  userInfo,
  getLink,
}: IFeedbackFormBaseProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isAgreementOpen, setIsAgreementOpen] = useState(false);

  const onModalClose = useCallback(() => setIsAgreementOpen(false), []);
  const onModalOpen = useCallback(() => setIsAgreementOpen(true), []);

  const signedLabel = (isSigned: boolean | null | undefined | string) => {
    return isSigned ? 'Подписано' : 'Не подписано';
  };

  function renderForm({ handleSubmit, form }: FormRenderProps<FeedbackFormType>) {
    if (knmData || planData) {
      const currentDateTime = formatDate({
        date: new Date(),
        dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
      });

      if (knmData) {
        form.change('topic', `Проблема с КНМ ${knmData.erpId}_${currentDateTime}`);
        form.change('knmId', knmData.id);
      }
      if (planData) {
        form.change('topic', `Проблема с планом КНМ ${planData.id}_${currentDateTime}`);
        form.change('planId', planData.id);
      }
    }
    dispatch(feedbackSetForm(form));

    return (
      <form onSubmit={handleSubmit}>
        <Row margin="medium">
          <Row title="Сведения об отправителе" notWrap>
            <FieldBlock size="col-3">
              <TextInputField
                name="lastName"
                placeholder="Фамилия"
                validate={composeValidators(
                  makeRequired('Поле не может быть пустым'),
                  makeMaxCharactersValidator(256, 'Превышена допустимая длина строки'),
                )}
              />
            </FieldBlock>
            <FieldBlock size="col-3">
              <TextInputField
                name="firstName"
                placeholder="Имя"
                validate={composeValidators(
                  makeRequired('Поле не может быть пустым'),
                  makeMaxCharactersValidator(256, 'Превышена допустимая длина строки'),
                )}
              />
            </FieldBlock>
            <FieldBlock size="col-3">
              <TextInputField
                name="patronymic"
                placeholder="Отчество"
                validate={composeValidators(makeMaxCharactersValidator(256, 'Превышена допустимая длина строки'))}
              />
            </FieldBlock>
          </Row>
          <Row notWrap>
            <FieldBlock size="col-3" margin="right">
              <TextInputField
                name="phone"
                placeholder="Телефон"
                isPhone
                validate={composeValidators(
                  makeMaxCharactersValidator(256, 'Превышена допустимая длина строки'),
                  makePhoneValidation('Введены недопустимые символы'),
                )}
              />
            </FieldBlock>
            <FieldBlock>
              <TextInputField
                name="email"
                placeholder="Электронная почта"
                validate={composeValidators(
                  makeRequired('Поле не может быть пустым'),
                  makeEmailValidation('Введены недопустимые символы'),
                  makeMaxCharactersValidator(256, 'Превышена допустимая длина строки'),
                )}
              />
            </FieldBlock>
          </Row>

          <Row notWrap>
            <FieldBlock size="col-3" margin="right">
              <SelectInputField
                name="declarantType"
                options={declarantTypes}
                placeholder="Тип заявителя"
                size="medium"
                isClearable={false}
                validate={makeRequired('Поле не может быть пустым')}
              />
            </FieldBlock>
            <FieldBlock>
              <TextInputField
                name="organizationName"
                placeholder="Наименование организации"
                validate={composeValidators(
                  makeRequired('Поле не может быть пустым'),
                  makeMaxCharactersValidator(1024, 'Превышена допустимая длина строки'),
                )}
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock>
              <TextInputField
                name="position"
                placeholder="Должность"
                validate={composeValidators(
                  makeRequired('Поле не может быть пустым'),
                  makeMaxCharactersValidator(600, 'Превышена допустимая длина строки'),
                )}
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Содержание обращения"></FieldBlock>
          </Row>
          {knmData && (
            <Row>
              <FieldBlock label="Номер паспорта КНМ" placementLabel="left" labelSize="col-4">
                <TextInput name="knmErpId" disabled={true} className="shared-row-field" value={knmData.erpId} />
              </FieldBlock>
            </Row>
          )}
          {planData && (
            <Row>
              <FieldBlock label="Номер плана КНМ" placementLabel="left" labelSize="col-4">
                <TextInput name="planId" disabled={true} className="shared-row-field" value={planData.id} />
              </FieldBlock>
            </Row>
          )}
          {(knmData || planData) && (
            <>
              <Row>
                <FieldBlock label={knmData ? 'Статус КНМ' : 'Статус плана КНМ'} placementLabel="left" labelSize="col-4">
                  <TextInput
                    name="status"
                    disabled={true}
                    className="shared-row-field"
                    value={knmData ? knmData.status : planData?.erknmStatus?.toString()}
                  />
                </FieldBlock>
              </Row>
              <Row>
                <FieldBlock label="Статус подписания" placementLabel="left" labelSize="col-4">
                  <TextInput
                    name="signed"
                    disabled={true}
                    className="shared-row-field"
                    value={signedLabel(knmData ? knmData.signed : planData?.signed)}
                  />
                </FieldBlock>
              </Row>
            </>
          )}
          {knmData && (
            <>
              <Row>
                <FieldBlock label="Характер КНМ" placementLabel="left" labelSize="col-4">
                  <TextInput name="typeErknm" disabled={true} className="shared-row-field" value={knmData.type} />
                </FieldBlock>
              </Row>
              <Row>
                <FieldBlock label="Вид КНМ" placementLabel="left" labelSize="col-4">
                  <TextInput name="knmType" disabled={true} className="shared-row-field" value={knmData.kindKnm} />
                </FieldBlock>
              </Row>
              <Row>
                <FieldBlock label="Необходимость согласования" placementLabel="left" labelSize="col-4">
                  <TextInput
                    name="approveRequired"
                    disabled={true}
                    className="shared-row-field"
                    value={approveRequiredSelectOptions.find(x => x.value === knmData.approveRequired)?.label}
                  />
                </FieldBlock>
              </Row>
            </>
          )}
          {(knmData || planData) && (
            <>
              <Row>
                <FieldBlock
                  label={knmData ? 'Ссылка на паспорт КНМ' : 'Ссылка на план КНМ'}
                  placementLabel="left"
                  labelSize="col-4"
                >
                  <TextInput name="link" disabled={true} className="shared-row-field" value={getLink()} />
                </FieldBlock>
              </Row>
              <Row>
                <FieldBlock label="Пользователь" placementLabel="left" labelSize="col-4">
                  <TextInput name="user" disabled={true} value={userInfo()} className="shared-row-field" />
                </FieldBlock>
              </Row>
            </>
          )}
          <Row>
            <FieldBlock>
              <TextInputField
                name="topic"
                placeholder="Тема обращения"
                validate={composeValidators(
                  makeRequired('Поле не может быть пустым'),
                  makeMaxCharactersValidator(1024, 'Превышена допустимая длина строки'),
                )}
                disabled={knmData !== undefined || planData !== undefined}
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock>
              <TextareaField
                inputClassName={styles.FieldDescription}
                name="text"
                placeholder="Описание"
                validate={composeValidators(
                  makeRequired('Поле не может быть пустым'),
                  makeMaxCharactersValidator(10000, 'Превышена допустимая длина строки'),
                )}
              />
            </FieldBlock>
          </Row>
          <Row>
            <UploadInput
              files={files}
              invalidExtensions={['xls', 'txt', 'rtf']}
              onChange={handleUploadInputChange}
              error={error}
              setError={setError}
            />
            <UploadStatus className={styles.UploadStatus} files={files} removeFile={removeFile} setError={setError} />
          </Row>
          <h3 className={generalStyles.Title}>
            Общие требования по оформлению обращений в Службу технической поддержки
          </h3>
          <div className={generalStyles.Description}>
            При направлении обращений в Службу технической поддержки следует указывать следующий минимальный набор
            сведений:
          </div>
          <ol className={generalStyles.List}>
            <li>ФИО и Роль пользователя в системе, под которым возникает ошибка;</li>
            <li>Полное наименование организации и региона РФ;</li>
            <li>Занимаемая должность в организации;</li>
            <li>Используемая система (ЕРП или ЕРКНМ);</li>
            <li>Номер паспорта проверки и/или плана проверок в системе;</li>
            <li>Пошаговое описание ситуации, приводящей к ошибке;</li>
            <li>Пошаговые иллюстрирующие скриншоты действий пользователя.</li>
          </ol>
          <div className={generalStyles.DescriptionLast}>
            А также иную дополнительную информацию, которая может более подробно описать Ваш вопрос.
          </div>
          <div className={generalStyles.Description}>
            Службой технической поддержки ФГИС &quot;Единый реестр контрольных (надзорный) мероприятий&quot; и ее
            подсистемы &quot;Единый реестр проверок&quot; не рассматриваются обращения, направленные через форму
            обратной связи или по электронной почте, в следующих случаях:
          </div>
          <ul className={generalStyles.List}>
            <li>При отсутствии минимального набора сведений, указанных выше;</li>
            <li>При указании неполного либо недостоверного адреса электронной почты;</li>
            <li>
              Если предоставление ответа требует разглашения сведений, составляющих государственную, налоговую или иную
              охраняемую федеральным законом тайну;
            </li>
            <li>
              Если обращение не поддаётся прочтению, включая случаи, когда текст написан не на русском языке, с
              использованием латиницы, либо не структурирован (не разбит на предложения/ отсутствуют пробелы);
            </li>
            <li>Если обращение не содержит конкретных заявлений, жалоб или предложений.</li>
          </ul>
          <Row>
            <CheckboxField
              name="agreement"
              label={
                <>
                  Я соглашаюсь на&nbsp;
                  <Button
                    variant="link"
                    type="button"
                    text="обработку персональных данных"
                    className={styles.AgreementButton}
                    onClick={onModalOpen}
                  />
                </>
              }
              size="medium"
              type="checkbox"
              theme="system"
              validate={makeRequired('Пожалуйста, согласитесь с условиями')}
            />
          </Row>
          <Button color="primary" size="medium" text="Отправить" type="submit" />
        </Row>
      </form>
    );
  }

  return (
    <div className={styles.FeedbackForm}>
      <FeedbackAgreementModal onClose={onModalClose} visible={isAgreementOpen} />
      <Form<FeedbackFormType>
        onSubmit={onFormSubmit}
        initialValues={initialFeedbackFormValues}
        render={renderForm}
        subscription={{}}
      />
    </div>
  );
};
